// per docs/caiet-uc-spec.md §UC22-01 §UC22-03
// per docs/shape-ops-correction-admin-register.md §2 D1-D5, D7 (T091; PERF-12, CF-63)
// The register of correction requests, per wireframes/react/src/pages/AdminSolicitari.tsx.
// It used to read PENDING only, so an approved or rejected request left the
// product the moment it was decided; the status filter now reaches all three and
// the decision is taken on its own page, not in a modal over six raw ids. It is
// read one page at a time, with every criterion and the order applied by the
// server, so a search or a sort holds over the whole register.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCriteriaParams } from '../../app/use-criteria-params';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudIcon, MudSelect, MudTable, MudTag } from '@egov-moldova/mud-react';
import {
  dateInputToIso,
  EmptyRegisterMessage,
  FilterBar,
  formatDate,
  formatDateTime,
  formatExpiry,
  isoToDateInput,
  MetadataLine,
  PageHeader,
  type ActiveFilter,
  onRowOpen,
  DraftDateInput,
} from '@simsm/ui-shared';
import { QueryState } from '../../components/QueryState/QueryState';
import { TableFooter, usePageSize } from '../../components/TableFooter/TableFooter';
import {
  listAdminCorrectionFacets,
  listAdminCorrections,
  locationFacets,
  statusLabelKey,
  statusSemantic,
  type AdminCorrectionSort,
} from '../../features/corrections/admin-api';
import { usePendingCorrectionsCount } from '../../features/corrections/use-pending-corrections-count';
import './corrections.css';

const STATUSES = ['PENDING', 'APPROVED', 'REJECTED'] as const;
const SORTS: readonly AdminCorrectionSort[] = ['newest', 'oldest', 'reporter', 'medicine', 'lot'];
// The search runs on the server now, so it waits for a word worth sending, as
// the history log does (CatalogHistoryLog MIN_QUERY_LENGTH, QUERY_DEBOUNCE_MS).
const MIN_QUERY_LENGTH = 2;
const QUERY_DEBOUNCE_MS = 250;

function committedSearch(draft: string): string {
  const trimmed = draft.trim();
  return trimmed.length >= MIN_QUERY_LENGTH ? trimmed : '';
}

function pageParam(value: string | null): number {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : 1;
}

export function CorrectionQueuePage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const [params, setParams] = useCriteriaParams();
  const [pageSize, setPageSize] = usePageSize('admin-corrections');

  // ACCS-11/12/13: the criteria live in the address, so the register is
  // bookmarkable and the browser's Back returns to the same view. The page is
  // one of them (D7).
  const status = params.get('status') ?? 'PENDING';
  const search = params.get('q') ?? '';
  const registrarId = params.get('registrar') ?? '';
  const siteId = params.get('site') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const sort = SORTS.find((s) => s === params.get('sort')) ?? 'newest';
  const page = pageParam(params.get('page'));

  // Any criterion returns the reader to the first page: page 4 of another
  // selection names nothing in this one. Each change is a step Back undoes
  // (ACCS-11); typing in the search box is one burst, one step.
  const setFilters = useCallback(
    (changes: Record<string, string>): void => {
      const next = new URLSearchParams(params);
      for (const [key, value] of Object.entries(changes)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      next.delete('page');
      setParams(next);
    },
    [params, setParams],
  );
  const setFilter = useCallback(
    (key: string, value: string): void => setFilters({ [key]: value }),
    [setFilters],
  );

  function setPage(next: number): void {
    const updated = new URLSearchParams(params);
    if (next > 1) updated.set('page', String(next));
    else updated.delete('page');
    setParams(updated);
  }

  // What the reader is typing, which is not yet what the query asks for.
  const [searchDraft, setSearchDraft] = useState(search);
  useEffect(() => {
    const committed = committedSearch(searchDraft);
    if (committed === search) return;
    const timer = setTimeout(() => setFilter('q', committed), QUERY_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchDraft, search, setFilter]);
  // The address moved without the box (Back, a reset): the box follows it.
  useEffect(() => {
    setSearchDraft((draft) => (committedSearch(draft) === search ? draft : search));
  }, [search]);

  const query = useQuery({
    queryKey: [
      'admin-corrections',
      { status, search, registrarId, siteId, from, to, sort, page, pageSize },
    ],
    queryFn: () =>
      listAdminCorrections({
        status,
        q: search,
        registrarId,
        siteId,
        from,
        to,
        sort,
        page,
        pageSize,
      }),
    placeholderData: keepPreviousData,
    staleTime: 10_000,
  });

  // The reporter and location options come from the requests themselves (D3),
  // so a filter can never select an empty result, whichever page is shown.
  const facetsQuery = useQuery({
    queryKey: ['admin-correction-facets'],
    queryFn: listAdminCorrectionFacets,
    staleTime: 60_000,
  });
  const { count: pendingCount } = usePendingCorrectionsCount(true);

  const rows = useMemo(() => query.data?.requests ?? [], [query.data]);
  const totalCount = query.data?.totalCount ?? 0;

  // A page past the end (rows decided since, a smaller page size) moves to the
  // last page that has rows.
  const lastPage = Math.max(1, Math.ceil(totalCount / pageSize));
  useEffect(() => {
    if (query.isSuccess && !query.isPlaceholderData && page > lastPage) {
      const updated = new URLSearchParams(params);
      if (lastPage > 1) updated.set('page', String(lastPage));
      else updated.delete('page');
      setParams(updated, { replace: true });
    }
  }, [query.isSuccess, query.isPlaceholderData, page, lastPage, params, setParams]);

  const facets = useMemo(() => facetsQuery.data ?? [], [facetsQuery.data]);
  const reporters = useMemo(() => {
    const byIdno = new Map<string, string>();
    for (const facet of facets) {
      if (!byIdno.has(facet.registrarId)) {
        byIdno.set(facet.registrarId, facet.registrarName || facet.registrarId);
      }
    }
    return [...byIdno.entries()];
  }, [facets]);
  // A Registrar's own locations once one is chosen; every location, named with
  // its Registrar, until then (CF-63: the reporter is both).
  const locations = useMemo(
    () =>
      locationFacets(facets, registrarId).map((facet) => {
        const site = facet.siteName || t('approvals.cell.unnamed_site');
        return {
          key: `${facet.registrarId}:${facet.siteId}`,
          value: facet.siteId,
          label: registrarId
            ? site
            : t('approvals.filter.location_option', {
                site,
                registrar: facet.registrarName || facet.registrarId,
              }),
        };
      }),
    [facets, registrarId, t],
  );

  function chooseRegistrar(next: string): void {
    // A location of another Registrar would select nothing.
    const keepsSite =
      !siteId ||
      facets.some((facet) => facet.siteId === siteId && (!next || facet.registrarId === next));
    setFilters({ registrar: next, ...(keepsSite ? {} : { site: '' }) });
  }

  const hasFilter = Boolean(search || registrarId || siteId || from || to || status !== 'PENDING');

  // PENDING is the queue's own default, so it is not a criterion until the
  // administrator moves off it. The search term gets no chip: it clears from the
  // box's own × (BaraFiltre.tsx:35).
  const activeFilters: ActiveFilter[] = [
    ...(status !== 'PENDING'
      ? [
          {
            key: 'status',
            label: t('approvals.filter.status'),
            value: status ? t(statusLabelKey(status)) : t('approvals.filter.status_all'),
            onRemove: () => setFilter('status', ''),
          },
        ]
      : []),
    ...(from || to
      ? [
          {
            key: 'period',
            label: t('approvals.filter.created'),
            value: t('common.filter.range_value', {
              from: isoToDateInput(from) || '…',
              to: isoToDateInput(to) || '…',
            }),
            onRemove: () => setFilters({ from: '', to: '' }),
          },
        ]
      : []),
    ...(registrarId
      ? [
          {
            key: 'registrar',
            label: t('approvals.filter.reporter'),
            value: reporters.find(([idno]) => idno === registrarId)?.[1] ?? registrarId,
            onRemove: () => chooseRegistrar(''),
          },
        ]
      : []),
    ...(siteId
      ? [
          {
            key: 'site',
            label: t('approvals.filter.location'),
            value:
              facets.find((facet) => facet.siteId === siteId)?.siteName ||
              t('approvals.cell.unnamed_site'),
            onRemove: () => setFilter('site', ''),
          },
        ]
      : []),
  ];

  const columns = [
    { key: 'created', label: t('approvals.column.created') },
    { key: 'reporter', label: t('approvals.column.reporter') },
    { key: 'medicine', label: t('approvals.column.medicine_lot') },
    { key: 'expiry', label: t('approvals.column.expiry') },
    { key: 'status', label: t('approvals.column.status') },
  ];

  const tableRows = useMemo(() => rows.map((row) => ({ id: row.id })), [rows]);

  // An empty register keeps its table. The wireframe leaves the head, the
  // frame and the footer standing and puts the message in one centred cell
  // spanning every column (AdminSolicitari.tsx:220); a box in
  // place of the register hides what the register would even hold. Both
  // QueryState branches render this same node, because mud-table draws its
  // `empty` slot on its own once `rows` is [].
  const register = (
    <>
      <div className="simsm-corrections__table">
        <MudTable
          className="simsm-table"
          columns={columns}
          rows={tableRows}
          hoverable={true}
          rowStyle="zebra"
          aria-label={t('approvals.table.aria_label')}
          onMudRowClick={onRowOpen<{ index: number; row: { id?: string | number } }>(
            (_index, { row }) => {
              // The list criteria travel with the row so the decision page's
              // "Inapoi la lista" returns to the same view (ACCS-11/12/13).
              const criteria = params.toString();
              if (row.id) {
                navigate(`/system-admin/approvals/${row.id}${criteria ? `?${criteria}` : ''}`);
              }
            },
          )}
        >
          <EmptyRegisterMessage>
            {hasFilter ? t('approvals.empty_filtered') : t('approvals.empty_pending')}
          </EmptyRegisterMessage>
          {rows.map((row, index) => [
            // The reporter is the Registrar and the location (CF-63), not the
            // person who asked: the row names no IDNP (D5).
            <span key={`created-${row.id}`} slot={`cell-created-${index}`}>
              {formatDateTime(row.createdAtIso)}
            </span>,
            <span
              key={`reporter-${row.id}`}
              slot={`cell-reporter-${index}`}
              className="simsm-corrections__stack"
            >
              <span>{row.registrarName || t('approvals.cell.unnamed_reporter')}</span>
              {row.siteId ? (
                <MetadataLine
                  wrap
                  items={[
                    {
                      label: t('approvals.cell.location'),
                      value: row.siteName || t('approvals.cell.unnamed_site'),
                      title: row.siteName,
                    },
                  ]}
                />
              ) : null}
            </span>,
            <span
              key={`medicine-${row.id}`}
              slot={`cell-medicine-${index}`}
              className="simsm-corrections__stack"
            >
              <span className="simsm-corrections__code">
                {row.medicineName || row.medicineCode}
              </span>
              <span className="simsm-corrections__meta">
                <span className="simsm-corrections__mono simsm-corrections__code">
                  {row.medicineCode}
                </span>
                {' · '}
                {t('approvals.cell.lot', { lot: row.lot })}
              </span>
            </span>,
            <span
              key={`expiry-${row.id}`}
              slot={`cell-expiry-${index}`}
              className="simsm-corrections__change"
            >
              <span>{formatExpiry(row.currentExpiry)}</span>
              <MudIcon name="arrow-right" size={16} aria-hidden="true" />
              <strong>{formatDate(row.decidedExpiry || row.proposedExpiry)}</strong>
              {row.decidedExpiry && row.decidedExpiry !== row.proposedExpiry ? (
                <span className="simsm-corrections__meta">
                  {t('approvals.cell.proposed_was', { date: formatDate(row.proposedExpiry) })}
                </span>
              ) : null}
            </span>,
            <span key={`status-${row.id}`} slot={`cell-status-${index}`}>
              <MudTag
                variant="status"
                semantic={statusSemantic(row.status)}
                label={t(statusLabelKey(row.status))}
              />
            </span>,
          ])}
        </MudTable>
      </div>
      {/* The shared footer of a paged register: the pager and "Rows 1–25 of N"
          (D7). Outside the frame, whose overflow would unstick it. */}
      <TableFooter
        total={totalCount}
        page={page}
        pageSize={pageSize}
        onPage={setPage}
        onPageSize={setPageSize}
      />
    </>
  );

  return (
    <section className="simsm-page simsm-corrections">
      <PageHeader title={t('approvals.title')} lede={t('approvals.lede')} />

      <FilterBar
        search={{
          value: searchDraft,
          onChange: setSearchDraft,
          placeholder: t('approvals.filter.search_placeholder'),
          ariaLabel: t('approvals.filter.search'),
          clearLabel: t('common.filter.clear_search'),
        }}
        active={activeFilters}
        onReset={() => {
          setSearchDraft('');
          setParams(new URLSearchParams());
        }}
        labels={{
          filters: t('common.filter.button'),
          reset: t('common.filter.reset'),
          panelAriaLabel: t('approvals.filters.aria_label'),
          activeAriaLabel: t('common.filter.active_aria_label'),
        }}
        panel={
          <>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('approvals.filter.status')}</span>
              <MudSelect
                size="medium"
                value={status}
                aria-label={t('approvals.filter.status')}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setFilter('status', e.detail.value)
                }
              >
                <option value="">{t('approvals.filter.status_all')}</option>
                {STATUSES.map((value) => (
                  <option key={value} value={value}>
                    {t(statusLabelKey(value))}
                  </option>
                ))}
              </MudSelect>
            </label>
            <div className="simsm-filter-bar__field-pair">
              <label className="simsm-filter-bar__field">
                <span className="simsm-filter-bar__field-label">
                  {t('approvals.filter.created_from')}
                </span>
                <DraftDateInput
                  placeholder={t('common.date.placeholder')}
                  size="md"
                  value={isoToDateInput(from)}
                  aria-label={t('approvals.filter.created_from')}
                  onMudChange={(e: CustomEvent<{ value: string }>) =>
                    setFilter('from', dateInputToIso(e.detail.value))
                  }
                />
              </label>
              <label className="simsm-filter-bar__field">
                <span className="simsm-filter-bar__field-label">
                  {t('approvals.filter.created_to')}
                </span>
                <DraftDateInput
                  placeholder={t('common.date.placeholder')}
                  size="md"
                  value={isoToDateInput(to)}
                  aria-label={t('approvals.filter.created_to')}
                  onMudChange={(e: CustomEvent<{ value: string }>) =>
                    setFilter('to', dateInputToIso(e.detail.value))
                  }
                />
              </label>
            </div>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">
                {t('approvals.filter.reporter')}
              </span>
              <MudSelect
                size="medium"
                value={registrarId}
                aria-label={t('approvals.filter.reporter')}
                onMudChange={(e: CustomEvent<{ value: string }>) => chooseRegistrar(e.detail.value)}
              >
                <option value="">{t('approvals.filter.reporter_all')}</option>
                {reporters.map(([idno, name]) => (
                  <option key={idno} value={idno}>
                    {name}
                  </option>
                ))}
              </MudSelect>
            </label>
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">
                {t('approvals.filter.location')}
              </span>
              <MudSelect
                size="medium"
                value={siteId}
                aria-label={t('approvals.filter.location')}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setFilter('site', e.detail.value)
                }
              >
                <option value="">{t('approvals.filter.location_all')}</option>
                {locations.map((option) => (
                  <option key={option.key} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </MudSelect>
            </label>
            {/* Ordering is not a criterion, so it carries no chip; it lives in
                the panel because the wireframe's row one holds nothing but the
                search and the button. */}
            <label className="simsm-filter-bar__field">
              <span className="simsm-filter-bar__field-label">{t('approvals.filter.sort')}</span>
              <MudSelect
                size="medium"
                value={sort}
                aria-label={t('approvals.filter.sort')}
                onMudChange={(e: CustomEvent<{ value: string }>) =>
                  setFilter('sort', e.detail.value)
                }
              >
                {SORTS.map((value) => (
                  <option key={value} value={value}>
                    {t(`approvals.sort.${value}`)}
                  </option>
                ))}
              </MudSelect>
            </label>
          </>
        }
      />

      {/* Counted by the server over the whole register (D4); the rows shown are
          the footer's "Rows 1–25 of N". */}
      <p className="simsm-corrections__count" role="status" aria-live="polite">
        {pendingCount === 1
          ? t('approvals.pending_count_one')
          : t('approvals.pending_count_other', { count: pendingCount })}
      </p>

      <QueryState query={query} isEmpty={rows.length === 0} empty={register}>
        {register}
      </QueryState>
    </section>
  );
}
